import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { issueItem, receiveItem } from '../../lib/api';
import type { InventoryItem } from '../../types';

interface Props {
  item: InventoryItem;
  mode: 'issue' | 'receive';
  onClose: () => void;
}

export default function IssueReceiveModal({ item, mode, onClose }: Props) {
  const [qty, setQty] = useState(1);
  const [notes, setNotes] = useState('');
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: () =>
      mode === 'issue'
        ? issueItem(item.id, qty, notes)
        : receiveItem(item.id, qty, notes),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['item', item.id] });
      qc.invalidateQueries({ queryKey: ['inventory'] });
      onClose();
    },
  });

  const isIssue = mode === 'issue';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200">
          <h3 className="text-base font-semibold text-gray-900">
            {isIssue ? 'Issue Part' : 'Receive Part'}
          </h3>
          <p className="text-sm text-gray-500 mt-0.5">{item.name}</p>
        </div>

        <div className="p-5 space-y-4">
          {/* Current qty */}
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Current stock</span>
            <span className="font-semibold">{item.qty} {item.unit_of_measure}</span>
          </div>

          {/* Qty input */}
          <div>
            <label className="label">Quantity to {isIssue ? 'Issue' : 'Receive'}</label>
            <input
              type="number"
              min={1}
              className="input"
              value={qty}
              onChange={(e) => setQty(Math.max(1, parseInt(e.target.value) || 1))}
            />
            <p className="text-xs text-gray-500 mt-1">
              New total: <strong>{isIssue ? Math.max(0, item.qty - qty) : item.qty + qty}</strong>
            </p>
          </div>

          {/* Notes */}
          <div>
            <label className="label">Notes (optional)</label>
            <textarea
              className="input resize-none"
              rows={2}
              placeholder="Reason, work order, etc."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          {mutation.isError && (
            <p className="text-sm text-red-600">{(mutation.error as Error).message}</p>
          )}
        </div>

        <div className="px-5 py-4 border-t border-gray-200 flex justify-end gap-2">
          <button className="btn-secondary" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </button>
          <button
            className={isIssue ? 'btn-danger' : 'btn-primary'}
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
          >
            {mutation.isPending ? 'Saving…' : isIssue ? 'Issue' : 'Receive'}
          </button>
        </div>
      </div>
    </div>
  );
}
